import { NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransformTitlePipe } from '../../../../../core/pipes/transform-title.pipe';
import { AuthService } from '../../../../auth/auth.service';

@Component({
  selector: 'app-activity-timeline',
  standalone: true,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule, TransformTitlePipe],
  templateUrl: './activity-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityTimelineComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() limit = 5;
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  activities: any[] = [];
  loading = false;
  error: string | null = null;

  ngOnInit(): void {
    if (!this.hasPermission) return;
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.http
      .get('api/activity', { params: { limit: String(this.limit) } })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res.record?.data ?? res.record ?? [];
          this.activities = Array.isArray(data) ? data : [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return dateStr.replace('T', ' ').slice(0, 16);
  }

  getActionIcon(action: string): string {
    const map: Record<string, string> = {
      created: 'add_circle',
      updated: 'edit',
      deleted: 'delete',
      approved: 'check_circle',
      rejected: 'cancel',
      exported: 'download',
      login: 'login',
    };
    return map[action?.toLowerCase()] ?? 'circle';
  }
}
